import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import omit from 'lodash/omit';
import { useContext, useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useFieldArray, useForm } from 'react-hook-form';
import { BsPlus, BsTrash } from 'react-icons/bs';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { ApiError, ProviderCredentialingEducation, ProviderService } from '../../../api';
import { Card } from '../../../app/components/Card';
import { FileUploader } from '../../../app/components/FileUploader';
import { FormFeedback } from '../../../app/components/FormFeedback';
import { FormFieldVisibility, FormLabel } from '../../../app/components/FormLabel';
import { ServerErrorsAlert } from '../../../app/components/ServerErrorsAlert';
import { UnsavedChangesFormPrompt } from '../../../app/components/UnsavedChangesFormPrompt';
import { FormSubmitContext } from '../../../app/contexts/FormSubmitContext';
import { useDocumentTitle } from '../../../app/hooks/use-document-title';
import { providerQueryKeys } from '../../../app/query-keys-factories/providers';
import { handleApiError } from '../../../app/utils/errorhandling';
import { generateShowFieldErrors } from '../../../utils/forms';
import { ProviderProfileCreateOutletContext } from '../../Registration/ProviderProfileCreate';
import { ProviderFormContainer } from './ProviderFormContainer';

const validationSchema = z
  .object({
    medical_diplomas: z.array(z.object({ file: z.string().url() })),
    residency_certificates: z.array(z.object({ file: z.string().url() })),
    internships: z.array(z.object({ file: z.string().url() })),
    fellowship_certificates: z.array(z.object({ file: z.string().url() })),
    ecfmgs: z.array(z.object({ file: z.string().url() })),
    cmes: z.array(z.object({ file: z.string().url() })),
    is_resident: z.boolean().optional(),
    resident_graduation_on: z.union([z.string(), z.null(), z.date()]).optional(),
  })
  .refine(
    (data) => {
      // If is_resident is true, graduation date is required
      if (data.is_resident) {
        return data.resident_graduation_on !== null && data.resident_graduation_on !== '';
      }
      return true;
    },
    {
      message: 'Graduation date is required for residents',
      path: ['resident_graduation_on'], // This specifies which field the error belongs to
    }
  );

export const ProviderEducationForm = () => {
  useDocumentTitle('Provider Education');
  const { formRef, onSuccess } = useOutletContext<ProviderProfileCreateOutletContext>() ?? {};
  const { isSubmitEnabled } = useContext(FormSubmitContext);
  const {
    clearErrors,
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitSuccessful, errors },
    register,
    watch,
    setValue,
  } = useForm({ resolver: zodResolver(validationSchema) });

  const {
    fields: medicalDiplomaFields,
    append: addMedicalDiploma,
    remove: removeMedicalDiploma,
  } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'medical_diplomas', // unique name for your Field Array
  });
  const {
    fields: residencyCertificateFields,
    append: addResidencyCertificate,
    remove: removeResidencyCertificate,
  } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'residency_certificates', // unique name for your Field Array
  });
  const {
    fields: internshipFields,
    append: addInternship,
    remove: removeInternship,
  } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'internships', // unique name for your Field Array
  });
  const {
    fields: fellowshipCertificateFields,
    append: addFellowshipCertificate,
    remove: removeFellowshipCertificate,
  } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'fellowship_certificates', // unique name for your Field Array
  });
  const {
    fields: cmeFields,
    append: addCme,
    remove: removeCme,
  } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'cmes', // unique name for your Field Array
  });
  const {
    fields: ecfmgFields,
    append: addEcfmg,
    remove: removeEcfmg,
  } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'ecfmgs', // unique name for your Field Array
  });

  const [showGlobalServerErrors, setShowGlobalServerErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState<ApiError>();
  const [showServerFieldErrors, setShowServerFieldErrors] = useState<{
    medical_diplomas?: { [key: string]: { file: boolean } };
    residency_certificates?: { [key: string]: { file: boolean } };
    internships?: { [key: string]: { file: boolean } };
    fellowship_certificates?: { [key: string]: { file: boolean } };
    ecfmgs?: { [key: string]: { file: boolean } };
    cmes?: { [key: string]: { file: boolean } };
    languages_spoken?: boolean;
    resident_graduation_on?: boolean;
  }>({});
  const { data: provider } = useQuery({
    queryKey: providerQueryKeys.detailCredentialingEducation(),
    queryFn: () => ProviderService.providerCurrentCredentialingEducationRetrieve(),
  });

  useEffect(() => {
    reset({
      ...provider,
    });
  }, [provider]);

  const queryClient = useQueryClient();
  const mutationProviderCurrentCredentialingEducationUpdate = useMutation({
    mutationFn: async (data: ProviderCredentialingEducation) => {
      await ProviderService.providerCurrentCredentialingEducationUpdate({ requestBody: data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(providerQueryKeys.all());
    },
  });

  function handleClearErrorsAlert() {
    setShowGlobalServerErrors(false);
  }

  const handleSubmitClick = () => {
    clearErrors();
    setShowGlobalServerErrors(false);
    setShowServerFieldErrors({});
  };

  const onSubmit = async (data) => {
    try {
      await mutationProviderCurrentCredentialingEducationUpdate.mutateAsync(data);
      toast.success('Provider Education Data Updated Successfully');
      onSuccess?.();
    } catch (errors) {
      if (errors.status === 400) {
        const fieldErrors = omit(errors.body, ['non_field_errors']);
        setServerErrors(errors);
        setShowServerFieldErrors(generateShowFieldErrors(fieldErrors));
        setShowGlobalServerErrors(true);
      }
      handleApiError(errors);
    }
  };

  return (
    <Card className="p-4">
      <h2 className="border-bottom pb-2 mb-4">Education</h2>
      <Form noValidate onSubmit={handleSubmit(onSubmit)} ref={formRef}>
        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            id="is_resident"
            label="Are you a resident?"
            {...register('is_resident')}
          />
        </Form.Group>

        {(watch('is_resident') || watch('resident_graduation_on')) && (
          <Form.Group className="mb-3">
            <FormLabel required={watch('is_resident')} visibility={FormFieldVisibility.CONNECTED}>
              Expected Graduation Date
            </FormLabel>
            <Form.Control
              type="date"
              {...register('resident_graduation_on')}
              isInvalid={!!errors.resident_graduation_on}
              onChange={(e) => {
                const value = e.target.value;
                setValue('resident_graduation_on', value || null, { shouldValidate: true });
              }}
            />
            <FormFeedback
              clientError={errors.resident_graduation_on}
              serverErrors={
                showServerFieldErrors.resident_graduation_on
                  ? serverErrors?.body?.resident_graduation_on
                  : null
              }
            />
          </Form.Group>
        )}

        <Form.Group className="mb-3 row" />
        <Form.Group className="mb-3">
          <FormLabel visibility={FormFieldVisibility.CONNECTED}>Medical Diplomas</FormLabel>
          <ul className="list-unstyled ps-5">
            {medicalDiplomaFields.map((item, index) => (
              <li key={item.id}>
                <Row>
                  <Col xs="auto" className="flex-grow-1">
                    <FileUploader
                      name={`medical_diplomas.${index}.file`}
                      control={control}
                      className="mb-3"
                      dest="providers_documents"
                      isClearable={false}
                      isInvalid={
                        !!errors.medical_diplomas?.[index]?.file ||
                        !!showServerFieldErrors.medical_diplomas?.[index.toString()]?.file
                      }
                    />
                    <FormFeedback
                      clientError={errors.medical_diplomas?.[index]?.file}
                      serverErrors={serverErrors?.body.medical_diplomas?.[index]?.file}
                    />
                  </Col>
                  <Col xs="auto">
                    <Button
                      variant="link"
                      className="text-danger"
                      size="lg"
                      type="button"
                      onClick={() => removeMedicalDiploma(index)}
                    >
                      <BsTrash />
                    </Button>
                  </Col>
                </Row>
              </li>
            ))}
          </ul>
          <Button
            className="d-flex align-items-center"
            type="button"
            variant="outline-primary"
            onClick={() => addMedicalDiploma({ file_type: 'MEDICAL_DIPLOMA' })}
          >
            <BsPlus className="me-1" /> Add Medical Diploma
          </Button>
        </Form.Group>
        <Form.Group className="mb-3">
          <FormLabel visibility={FormFieldVisibility.CONNECTED}>Residency Certificates</FormLabel>
          <ul className="list-unstyled ps-5">
            {residencyCertificateFields.map((item, index) => (
              <li key={item.id}>
                <Row>
                  <Col xs="auto" className="flex-grow-1">
                    <FileUploader
                      name={`residency_certificates.${index}.file`}
                      control={control}
                      className="mb-3"
                      dest="providers_documents"
                      isClearable={false}
                      isInvalid={
                        !!errors.residency_certificates?.[index]?.file ||
                        !!showServerFieldErrors.residency_certificates?.[index.toString()]?.file
                      }
                    />
                    <FormFeedback
                      clientError={errors.residency_certificates?.[index]?.file}
                      serverErrors={serverErrors?.body.residency_certificates?.[index]?.file}
                    />
                  </Col>
                  <Col xs="auto">
                    <Button
                      variant="link"
                      className="text-danger"
                      size="lg"
                      type="button"
                      onClick={() => removeResidencyCertificate(index)}
                    >
                      <BsTrash />
                    </Button>
                  </Col>
                </Row>
              </li>
            ))}
          </ul>
          <Button
            className="d-flex align-items-center"
            variant="outline-primary"
            type="button"
            onClick={() => addResidencyCertificate({})}
          >
            <BsPlus className="me-1" /> Add Residency Certificate
          </Button>
        </Form.Group>
        <Form.Group className="mb-3">
          <FormLabel visibility={FormFieldVisibility.CONNECTED}>Internships</FormLabel>
          <ul className="list-unstyled ps-5">
            {internshipFields.map((item, index) => (
              <li key={item.id}>
                <Row>
                  <Col xs="auto" className="flex-grow-1">
                    <FileUploader
                      name={`internships.${index}.file`}
                      control={control}
                      className="mb-3"
                      dest="providers_documents"
                      isClearable={false}
                      isInvalid={
                        !!errors.internships?.[index]?.file ||
                        !!showServerFieldErrors.internships?.[index.toString()]?.file
                      }
                    />
                    <FormFeedback
                      clientError={errors.internships?.[index]?.file}
                      serverErrors={serverErrors?.body.internships?.[index]?.file}
                    />
                  </Col>
                  <Col xs="auto">
                    <Button
                      variant="link"
                      className="text-danger"
                      size="lg"
                      type="button"
                      onClick={() => removeInternship(index)}
                    >
                      <BsTrash />
                    </Button>
                  </Col>
                </Row>
              </li>
            ))}
          </ul>
          <Button
            className="d-flex align-items-center"
            variant="outline-primary"
            type="button"
            onClick={() => addInternship({})}
          >
            <BsPlus className="me-1" /> Add Internships
          </Button>
        </Form.Group>
        <Form.Group className="mb-3">
          <FormLabel visibility={FormFieldVisibility.CONNECTED}>Fellowship Certificates</FormLabel>
          <ul className="list-unstyled ps-5">
            {fellowshipCertificateFields.map((item, index) => (
              <li key={item.id}>
                <Row>
                  <Col xs="auto" className="flex-grow-1">
                    <FileUploader
                      name={`fellowship_certificates.${index}.file`}
                      control={control}
                      className="mb-3"
                      dest="providers_documents"
                      isClearable={false}
                      isInvalid={
                        !!errors.fellowship_certificates?.[index]?.file ||
                        !!showServerFieldErrors.fellowship_certificates?.[index.toString()]?.file
                      }
                    />
                    <FormFeedback
                      clientError={errors.fellowship_certificates?.[index]?.file}
                      serverErrors={serverErrors?.body.fellowship_certificates?.[index]?.file}
                    />
                  </Col>
                  <Col xs="auto">
                    <Button
                      variant="link"
                      className="text-danger"
                      size="lg"
                      type="button"
                      onClick={() => removeFellowshipCertificate(index)}
                    >
                      <BsTrash />
                    </Button>
                  </Col>
                </Row>
              </li>
            ))}
          </ul>
          <Button
            className="d-flex align-items-center"
            variant="outline-primary"
            type="button"
            onClick={() => addFellowshipCertificate({})}
          >
            <BsPlus className="me-1" /> Add Fellowship Certificate
          </Button>
        </Form.Group>
        <Form.Group className="mb-3">
          <FormLabel visibility={FormFieldVisibility.CONNECTED}>ECFMGs</FormLabel>
          <ul className="list-unstyled ps-5">
            {ecfmgFields.map((item, index) => (
              <li key={item.id}>
                <Row>
                  <Col xs="auto" className="flex-grow-1">
                    <FileUploader
                      name={`ecfmgs.${index}.file`}
                      control={control}
                      className="mb-3"
                      dest="providers_documents"
                      isClearable={false}
                      isInvalid={
                        !!errors.ecfmgs?.[index]?.file ||
                        !!showServerFieldErrors.ecfmgs?.[index.toString()]?.file
                      }
                    />
                    <FormFeedback
                      clientError={errors.ecfmgs?.[index]?.file}
                      serverErrors={serverErrors?.body.ecfmgs?.[index]?.file}
                    />
                  </Col>
                  <Col xs="auto">
                    <Button
                      variant="link"
                      className="text-danger"
                      size="lg"
                      type="button"
                      onClick={() => removeEcfmg(index)}
                    >
                      <BsTrash />
                    </Button>
                  </Col>
                </Row>
              </li>
            ))}
          </ul>
          <Button
            className="d-flex align-items-center"
            variant="outline-primary"
            type="button"
            onClick={() => addEcfmg({})}
          >
            <BsPlus className="me-1" /> Add ECFMG
          </Button>
        </Form.Group>
        <Form.Group className="mb-3">
          <FormLabel visibility={FormFieldVisibility.CONNECTED}>CMEs</FormLabel>
          <ul className="list-unstyled ps-5">
            {cmeFields.map((item, index) => (
              <li key={item.id}>
                <Row>
                  <Col xs="auto" className="flex-grow-1">
                    <FileUploader
                      name={`cmes.${index}.file`}
                      control={control}
                      className="mb-3"
                      dest="providers_documents"
                      isClearable={false}
                      isInvalid={
                        !!errors.cmes?.[index]?.file ||
                        !!showServerFieldErrors.cmes?.[index.toString()]?.file
                      }
                    />
                    <FormFeedback
                      clientError={errors.cmes?.[index]?.file}
                      serverErrors={serverErrors?.body.cmes?.[index]?.file}
                    />
                  </Col>
                  <Col xs="auto">
                    <Button
                      variant="link"
                      className="text-danger"
                      size="lg"
                      type="button"
                      onClick={() => removeCme(index)}
                    >
                      <BsTrash />
                    </Button>
                  </Col>
                </Row>
              </li>
            ))}
          </ul>
          <Button
            className="d-flex align-items-center"
            variant="outline-primary"
            type="button"
            onClick={() => addCme({})}
          >
            <BsPlus className="me-1" /> Add CME
          </Button>
        </Form.Group>
        <Row>
          <Col md={12}>
            <Button
              className="float-end"
              variant="primary"
              type="submit"
              onClick={() => handleSubmitClick()}
              disabled={
                mutationProviderCurrentCredentialingEducationUpdate.isLoading || !isSubmitEnabled
              }
            >
              Save Changes
            </Button>
          </Col>
        </Row>
        <ServerErrorsAlert
          errors={serverErrors}
          showErrors={showGlobalServerErrors}
          clearErrors={handleClearErrorsAlert}
        />
      </Form>
      <UnsavedChangesFormPrompt hasUnsavedChanges={isDirty && !isSubmitSuccessful} />
    </Card>
  );
};

export const ProviderEducation = () => {
  return (
    <ProviderFormContainer>
      <ProviderEducationForm />
    </ProviderFormContainer>
  );
};
